import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

const VIEWS = [
  {
    url: 'https://customer-assets.emergentagent.com/job_biotech-labs-1/artifacts/azmwc9dd_A5C41689-7B97-4BFD-B3F3-ABBDF0FE3948.PNG',
    label: 'Product Overview',
    sub: 'Isolated form-factor view',
    tag: '01 / 04',
    points: [
      { text: 'Neural Interface Core', x: '18%', y: '32%' },
      { text: 'EMG Sensor Array', x: '42%', y: '58%' },
      { text: 'Edge AI Processor', x: '65%', y: '40%' },
    ],
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_biotech-labs-1/artifacts/srnkacb3_99188951-3686-42B5-A0D1-23C98546BE01.PNG',
    label: 'Worn Configuration',
    sub: 'Human interface integration',
    tag: '02 / 04',
    points: [
      { text: 'Biometric Display', x: '20%', y: '30%' },
      { text: 'Precision Fit System', x: '50%', y: '60%' },
      { text: 'Real-time Monitoring', x: '72%', y: '45%' },
    ],
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_biotech-labs-1/artifacts/jq4qxiur_A26A9CCA-22A5-4B40-946A-21C038A128EB.PNG',
    label: 'Performance Mode',
    sub: 'Active use — motor control',
    tag: '03 / 04',
    points: [
      { text: 'Motor Control Interface', x: '22%', y: '35%' },
      { text: 'Adaptive Response AI', x: '55%', y: '55%' },
      { text: 'Live Signal Feed', x: '70%', y: '35%' },
    ],
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_biotech-labs-1/artifacts/gyitspvh_76B0BC53-CBE9-4FCA-A9D2-54B24F086211.PNG',
    label: 'Internal Architecture',
    sub: 'Exploded component view',
    tag: '04 / 04',
    points: [
      { text: '64-Ch Sensor Array', x: '15%', y: '45%' },
      { text: 'Modular PCB Stack', x: '45%', y: '38%' },
      { text: 'Power Management', x: '75%', y: '50%' },
    ],
  },
];

export default function BioSleeveViewer() {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [showPoints, setShowPoints] = useState(true);
  const containerRef = useRef(null);

  /* Auto-rotate */
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => setActive((i) => (i + 1) % VIEWS.length), 4000);
    return () => clearInterval(t);
  }, [auto]);

  /* Mouse tilt for 3D effect */
  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setTilt({ x, y });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  const prev = () => { setAuto(false); setActive((i) => (i - 1 + VIEWS.length) % VIEWS.length); };
  const next = () => { setAuto(false); setActive((i) => (i + 1) % VIEWS.length); };

  const current = VIEWS[active];

  return (
    <div className="mt-14 pt-10 border-t border-bvx-border" data-testid="biosleeve-3d-viewer">
      {/* Header row */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="font-rajdhani font-semibold uppercase tracking-widest text-bvx-cyan text-xs">
            Interactive Showcase
          </span>
          <h3 className="font-outfit font-bold text-white text-2xl mt-1">
            BioSleeve — 3D Model Viewer
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPoints(!showPoints)}
            className={`text-xs font-rajdhani font-semibold uppercase tracking-widest px-4 py-2 rounded-full border transition-colors duration-200 ${
              showPoints ? 'bg-bvx-green/15 border-bvx-green/40 text-bvx-green' : 'border-bvx-border text-white/40 hover:text-white'
            }`}
            data-testid="toggle-hotspots"
          >
            Hotspots
          </button>
          <button
            onClick={() => setAuto(!auto)}
            className={`flex items-center gap-1.5 text-xs font-rajdhani font-semibold uppercase tracking-widest px-4 py-2 rounded-full border transition-colors duration-200 ${
              auto ? 'bg-bvx-cyan/15 border-bvx-cyan/40 text-bvx-cyan' : 'border-bvx-border text-white/40 hover:text-white'
            }`}
            data-testid="toggle-autoplay"
          >
            {auto ? <Pause size={11} /> : <Play size={11} />}
            {auto ? 'Auto' : 'Manual'}
          </button>
        </div>
      </div>

      {/* Viewer wrapper — 3D perspective container */}
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="relative rounded-2xl overflow-hidden bg-black cursor-crosshair select-none"
        style={{ perspective: '1400px' }}
        data-testid="viewer-container"
      >
        {/* 3D tilt wrapper */}
        <div
          style={{
            transform: `perspective(1400px) rotateY(${tilt.x * 10}deg) rotateX(${-tilt.y * 6}deg)`,
            transition: 'transform 0.15s ease-out',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Image */}
          <AnimatePresence mode="wait">
            <motion.img
              key={active}
              src={current.url}
              alt={current.label}
              initial={{ opacity: 0, scale: 1.04, filter: 'brightness(0.5)' }}
              animate={{ opacity: 1, scale: 1, filter: 'brightness(1)' }}
              exit={{ opacity: 0, scale: 0.97, filter: 'brightness(0.5)' }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              className="w-full object-cover"
              style={{ height: '520px', objectFit: 'cover', objectPosition: 'center' }}
            />
          </AnimatePresence>

          {/* Scanline overlay */}
          <div
            className="absolute inset-0 pointer-events-none opacity-20"
            style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,240,255,0.03) 2px, rgba(0,240,255,0.03) 4px)',
            }}
          />

          {/* Radial glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 70% at 50% 50%, rgba(0,240,255,0.06) 0%, transparent 70%)' }}
          />
        </div>

        {/* ── HUD Overlays (outside tilt so they stay flat) ── */}

        {/* Corner brackets */}
        {[
          'top-4 left-4 border-t-2 border-l-2',
          'top-4 right-4 border-t-2 border-r-2',
          'bottom-4 left-4 border-b-2 border-l-2',
          'bottom-4 right-4 border-b-2 border-r-2',
        ].map((cls, i) => (
          <div key={i} className={`absolute w-6 h-6 ${cls} border-bvx-cyan pointer-events-none`} />
        ))}

        {/* Top tag */}
        <div className="absolute top-5 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="glass px-4 py-1.5 rounded-full flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-bvx-cyan animate-pulse" />
            <span className="text-bvx-cyan text-xs font-rajdhani font-semibold uppercase tracking-widest">
              {current.tag}
            </span>
          </div>
        </div>

        {/* Hotspot data points */}
        <AnimatePresence>
          {showPoints && current.points.map((pt, i) => (
            <motion.div
              key={`${active}-${i}`}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ delay: i * 0.12, duration: 0.3 }}
              className="absolute pointer-events-none"
              style={{ left: pt.x, top: pt.y }}
            >
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-3 h-3 rounded-full bg-bvx-green border border-bvx-green/60" />
                  <div className="absolute inset-0 rounded-full bg-bvx-green/40 animate-ping" />
                </div>
                <div className="glass px-3 py-1 rounded-lg">
                  <span className="text-white text-xs font-inter-tight whitespace-nowrap">{pt.text}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Bottom label */}
        <div className="absolute bottom-5 right-5 glass px-5 py-3 rounded-xl pointer-events-none">
          <p className="font-outfit font-bold text-white text-sm">{current.label}</p>
          <p className="text-white/50 text-xs font-inter-tight mt-0.5">{current.sub}</p>
        </div>

        {/* View counter */}
        <div className="absolute bottom-5 left-5 glass px-4 py-2 rounded-lg pointer-events-none">
          <div className="flex gap-1.5">
            {VIEWS.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${i === active ? 'w-6 bg-bvx-cyan' : 'w-2 bg-white/20'}`}
              />
            ))}
          </div>
        </div>

        {/* Nav arrows */}
        <button
          onClick={prev}
          data-testid="viewer-prev"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 glass rounded-full flex items-center justify-center text-white/60 hover:text-bvx-cyan transition-colors duration-200"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={next}
          data-testid="viewer-next"
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 glass rounded-full flex items-center justify-center text-white/60 hover:text-bvx-cyan transition-colors duration-200"
        >
          <ChevronRight size={18} />
        </button>

        {/* Drag hint */}
        <div className="absolute top-5 right-5 glass px-3 py-1.5 rounded-lg pointer-events-none">
          <span className="text-white/35 text-xs font-inter-tight">Move cursor to tilt</span>
        </div>
      </div>

      {/* Thumbnail strip */}
      <div className="grid grid-cols-4 gap-3 mt-4">
        {VIEWS.map((v, i) => (
          <button
            key={i}
            onClick={() => { setAuto(false); setActive(i); }}
            data-testid={`thumb-${i}`}
            className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 group ${
              i === active
                ? 'border-bvx-cyan shadow-neon-cyan'
                : 'border-bvx-border hover:border-white/30'
            }`}
          >
            <img
              src={v.url}
              alt={v.label}
              className={`w-full h-20 object-cover transition-opacity duration-200 ${i === active ? 'opacity-100' : 'opacity-55 group-hover:opacity-80'}`}
            />
            <div className={`absolute inset-0 flex items-end p-2 ${i === active ? 'bg-gradient-to-t from-black/70 to-transparent' : ''}`}>
              {i === active && (
                <span className="text-white text-xs font-inter-tight leading-tight line-clamp-1">{v.label}</span>
              )}
            </div>
            {i === active && (
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-bvx-cyan to-bvx-green" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
